import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { formatIndianCurrency } from '../../utils/formatters';
import { Users, Search, Plus, Ban, Check, X } from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, approved
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', phone: '', password: '' });

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', { params: search ? { search } : {} });
      let allUsers = res.data;

      // Apply status filter
      if (filter === 'pending') {
        allUsers = allUsers.filter(u => u.status === 'pending');
      } else if (filter === 'approved') {
        allUsers = allUsers.filter(u => u.status === 'approved');
      }

      setUsers(allUsers);
    } catch (err) {
      toast.error('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const validateUserForm = () => {
    if (!newUser.name.trim()) return 'Name is required';
    if (!newUser.email.trim()) return 'Email is required';
    if (!/\S+@\S+\.\S+/.test(newUser.email)) return 'Invalid email address';
    if (!/^\d{10}$/.test(newUser.phone)) return 'Phone number must be 10 digits';
    if (!newUser.password || newUser.password.length < 6)
      return 'Password must be at least 6 characters';
    return null;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const validationError = validateUserForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const res = await api.post('/admin/users/create', newUser);
      toast.success(res.data?.message || 'User created successfully!');
      setShowCreateForm(false);
      setNewUser({ name: '', email: '', phone: '', password: '' });
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create user';
      toast.error(message);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) return toast.error('Please select users first');
    if (!window.confirm(`Are you sure to ${action} ${selectedUsers.length} users?`)) return;

    try {
      await api.post('/admin/users/bulk-action', { user_ids: selectedUsers, action });
      toast.success(`Bulk ${action} completed successfully`);
      setSelectedUsers([]);
      fetchUsers();
    } catch {
      toast.error(`Failed to ${action} users`);
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedUsers(
      selectedUsers.length === users.length ? [] : users.map(u => u.id)
    );
  };

  const handleApproveRegistration = async (userId, approve) => {
    try {
      await api.post(`/admin/users/${userId}/approve-registration`, null, { params: { approve } });
      toast.success(`User ${approve ? 'approved' : 'rejected'} successfully`);
      fetchUsers();
    } catch {
      toast.error('Failed to process registration');
    }
  };

  // <-- UPDATED: Optimistic update and read taiId from response
  const handleToggleActive = async (userId, active) => {
    try {
      const res = await api.post(`/admin/users/${userId}/toggle-active`, null, { params: { active } });

      // backend should return { message, status, taiId }
      const taiId = res.data?.taiId;
      const status = res.data?.status ?? (active ? 'approved' : 'pending');

      // Optimistically update single user in state
      setUsers(prev =>
        prev.map(u =>
          u.id === userId ? { ...u, active, status, taiId: taiId ?? u.taiId } : u
        )
      );

      toast.success(`User ${active ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.detail || 'Failed to update user status';
      toast.error(message);
    }
  };

  return (
    <Layout>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900">Users Management</h1>
            <p className="text-gray-600 mt-1">{users.length} total users</p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold px-6 py-3 rounded-lg"
          >
            {showCreateForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {showCreateForm ? 'Cancel' : 'Create User'}
          </button>
        </div>

        {/* Create User Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser} className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Full Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
              />
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
              />
              <input
                type="tel"
                placeholder="Phone (10 digits)"
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
              />
              <input
                type="password"
                placeholder="Password (min 6 chars)"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                className="col-span-2 bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg"
              >
                Create User
              </button>
            </form>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {['all', 'pending', 'approved'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filter === f
                  ? f === 'pending'
                    ? 'bg-warning text-white'
                    : 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {f === 'all' ? 'All Users' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bg-primary-light border border-primary rounded-lg p-4 flex justify-between items-center">
            <span className="font-semibold text-primary">{selectedUsers.length} users selected</span>
            <div className="flex gap-2">
              <button onClick={() => handleBulkAction('approve')} className="bg-success text-white px-4 py-2 rounded-lg text-sm font-semibold">
                Approve
              </button>
              <button onClick={() => handleBulkAction('activate')} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold">
                Activate
              </button>
              <button onClick={() => handleBulkAction('deactivate')} className="bg-warning text-white px-4 py-2 rounded-lg text-sm font-semibold">
                Deactivate
              </button>
              <button onClick={() => handleBulkAction('delete')} className="bg-error text-white px-4 py-2 rounded-lg text-sm font-semibold">
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, or referral code..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
            />
          </div>
          <button type="submit" className="bg-primary hover:bg-primary-hover text-white font-semibold px-6 py-3 rounded-lg">
            Search
          </button>
        </form>

        {/* Users Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-10 text-gray-500">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No users found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === users.length && users.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-primary"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">KYC</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">TFP ID</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Invested</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Balance</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleSelectUser(user.id)}
                          className="w-4 h-4 text-primary"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{user.referralCode}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <p>{user.email}</p>
                        <p>{user.phone}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            user.status === 'approved'
                              ? 'bg-success-light text-success'
                              : user.status === 'pending'
                              ? 'bg-warning-light text-warning'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {user.status ?? 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            user.kycStatus === 'Approved'
                              ? 'bg-success-light text-success'
                              : user.kycStatus === 'Pending'
                              ? 'bg-warning-light text-warning'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {user.kycStatus ?? 'Not Started'}
                        </span>
                      </td>

                      {/* TAI ID column */}
                      <td className="px-6 py-4 font-mono text-sm">
                        {user.taiId ?? '-'}
                      </td>

                      <td className="px-6 py-4 text-right font-mono text-sm">
                        {formatIndianCurrency(user.totalInvested || 0)}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-sm">
                        {formatIndianCurrency(
                          (user.capitalBalance || 0) + (user.earningsBalance || 0)
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {user.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => handleApproveRegistration(user.id, true)}
                                className="p-2 bg-success-light text-success rounded-lg hover:bg-success hover:text-white"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleApproveRegistration(user.id, false)}
                                className="p-2 bg-error-light text-error rounded-lg hover:bg-error hover:text-white"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleToggleActive(user.id, !(user.active ?? true))}
                              className={`p-2 rounded-lg ${
                                user.active === false
                                  ? 'bg-success-light text-success'
                                  : 'bg-error-light text-error'
                              }`}
                            >
                              {user.active === false ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminUsers;
